/**
 * Admin → Short Links (A7Y-5290).
 *
 * Yevhen creates `www.apptonomy.ai/go/<word>` here and pastes it into a post or
 * comment; the link opens the chosen page carrying the three tags, so Mixpanel
 * counts the visit exactly as it counts the long tagged URL. The table lists
 * every link with what it brought, read from Mixpanel by `/admin/short-links/stats`.
 *
 * The counts load separately from the list, so a slow Mixpanel export never
 * hides the links; when they cannot be read the page says why instead of
 * showing zeros.
 */
import { PageHeader } from '@/components/PageHeader';
import { CopyButton } from '@/components/CopyButton';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { crmErrorMessage } from '@/pages/cmo/crm/crmErrors';
import {
  Button,
  Description,
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
  ErrorMessage,
  Field,
  Input,
  Label,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@a7y/tailwind-catalyst';
import {
  buildShortLinkTargetPath,
  SHORT_LINK_DEFAULT_DESTINATION,
  SHORT_LINK_PATH_PREFIX,
  validateShortLinkDestination,
  validateShortLinkSlug,
  validateShortLinkTag,
  type AdminShortLink,
  type AdminShortLinkStatsResponse,
  type ShortLinkCreateRequest,
} from '@a7y/types';
import { PlusIcon } from '@heroicons/react/16/solid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2 } from 'lucide-react';
import { useState, type ChangeEvent, type FormEvent } from 'react';

/** Pages a link usually opens. "Another page" takes any path on the site. */
const SHORT_LINK_PAGE_CHOICES = [
  { value: SHORT_LINK_DEFAULT_DESTINATION, label: 'Quick audit (home page)' },
  { value: '/check-my-app', label: 'Check my app' },
  { value: '/pricing', label: 'Pricing' },
] as const;
const OTHER_PAGE = '__other__';

const LIST_KEY = ['short-links'];
const STATS_KEY = ['short-links', 'stats'];

type StatsCell = { kind: 'loading' } | { kind: 'unavailable' } | { kind: 'value'; value: number };

function statCell(
  stats: AdminShortLinkStatsResponse | undefined,
  isLoading: boolean,
  slug: string,
  field: 'visitors' | 'quickAudits' | 'signups'
): StatsCell {
  if (isLoading) return { kind: 'loading' };
  if (!stats || stats.state !== 'ok') return { kind: 'unavailable' };
  return { kind: 'value', value: stats.counts[slug]?.[field] ?? 0 };
}

function StatValue({ cell }: { cell: StatsCell }) {
  if (cell.kind === 'loading') return <span className="text-zinc-400">…</span>;
  if (cell.kind === 'unavailable') {
    return (
      <span className="text-zinc-400" title="Not available — see the note above the table">
        n/a
      </span>
    );
  }
  return <span className="tabular-nums">{cell.value.toLocaleString()}</span>;
}

export function ShortLinksPage() {
  const queryClient = useQueryClient();
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const list = useQuery({ queryKey: LIST_KEY, queryFn: () => api.getShortLinks() });
  const stats = useQuery({ queryKey: STATS_KEY, queryFn: () => api.getShortLinkStats() });

  const refresh = useMutation({
    mutationFn: () => api.getShortLinkStats(true),
    onSuccess: (data) => queryClient.setQueryData(STATS_KEY, data),
  });

  const links: AdminShortLink[] = list.data?.links ?? [];
  // The refresh writes into the query cache, so the query is the one source of the counts.
  const statsData = stats.data;
  const statsLoading = stats.isLoading || refresh.isPending;
  const statsNote = refresh.isError
    ? `Refreshing the counts failed: ${crmErrorMessage(refresh.error)}`
    : stats.isError
    ? `The counts could not be loaded: ${crmErrorMessage(stats.error)}`
    : statsData?.state === 'unavailable'
      ? `The counts are not available: ${statsData.reason}`
      : null;

  return (
    <div className="px-4 py-0">
      <PageHeader
        title="Short Links"
        subtitle="Short tracked links on apptonomy.ai for posts and comments. Each one opens a page with its source, medium and campaign attached."
      >
        <Button color="emerald" onClick={() => setIsCreateOpen(true)}>
          <PlusIcon />
          New short link
        </Button>
      </PageHeader>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3 px-2 text-sm text-zinc-500 dark:text-zinc-400">
        <p>
          Visitors, quick audits and sign-ups come from Mixpanel and count what happened after each link
          was created.
          {statsData?.state === 'ok' && (
            <> Covers {statsData.window.from} to {statsData.window.to}.</>
          )}
        </p>
        <Button plain onClick={() => refresh.mutate()} disabled={statsLoading}>
          {refresh.isPending ? 'Refreshing…' : 'Refresh counts'}
        </Button>
      </div>

      {statsNote && (
        <div
          role="status"
          className="mb-3 rounded-lg bg-amber-50 px-4 py-2.5 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
        >
          {statsNote}
        </div>
      )}

      {list.isError ? (
        <div role="alert" className="rounded-lg bg-red-50 px-4 py-2.5 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
          The short links could not be loaded: {crmErrorMessage(list.error)}
        </div>
      ) : (
        <Table dense striped>
          <TableHead>
            <TableRow>
              <TableHeader>Short link</TableHeader>
              <TableHeader>Opens</TableHeader>
              <TableHeader>Source</TableHeader>
              <TableHeader>Medium</TableHeader>
              <TableHeader>Campaign</TableHeader>
              <TableHeader className="text-right">Visitors</TableHeader>
              <TableHeader className="text-right">Quick audits</TableHeader>
              <TableHeader className="text-right">Sign-ups</TableHeader>
              <TableHeader>Created</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.isLoading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center">
                  <div className="mx-auto h-6 w-6 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 dark:border-zinc-600" />
                </TableCell>
              </TableRow>
            ) : links.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="py-8 text-center">
                  <Link2 className="mx-auto h-10 w-10 text-zinc-400" />
                  <p className="mt-2 text-zinc-500">No short links yet</p>
                  <p className="text-sm text-zinc-400">Create one to share in a post or comment</p>
                </TableCell>
              </TableRow>
            ) : (
              links.map((link) => (
                <TableRow key={link.slug}>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-zinc-900 dark:text-white">
                        {link.shortUrl.replace(/^https:\/\//, '')}
                      </span>
                      <CopyButton value={link.shortUrl} label={`short link ${link.slug}`} />
                    </div>
                  </TableCell>
                  <TableCell>
                    <a
                      href={link.targetUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-zinc-600 underline decoration-zinc-300 underline-offset-2 hover:text-zinc-900 dark:text-zinc-300"
                    >
                      {link.destinationPath}
                    </a>
                  </TableCell>
                  <TableCell>{link.utmSource}</TableCell>
                  <TableCell>{link.utmMedium}</TableCell>
                  <TableCell>{link.utmCampaign}</TableCell>
                  {(['visitors', 'quickAudits', 'signups'] as const).map((field) => (
                    <TableCell key={field} className="text-right">
                      <StatValue cell={statCell(statsData, statsLoading, link.slug, field)} />
                    </TableCell>
                  ))}
                  <TableCell className="whitespace-nowrap text-zinc-500">{formatDate(link.createdAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      <CreateShortLinkDialog
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={() => {
          setIsCreateOpen(false);
          void queryClient.invalidateQueries({ queryKey: LIST_KEY });
        }}
      />
    </div>
  );
}

type FieldErrors = Partial<Record<'slug' | 'page' | 'utmSource' | 'utmMedium' | 'utmCampaign', string>>;

export function CreateShortLinkDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (link: AdminShortLink) => void;
}) {
  const [slug, setSlug] = useState('');
  const [pageChoice, setPageChoice] = useState<string>(SHORT_LINK_DEFAULT_DESTINATION);
  const [otherPage, setOtherPage] = useState('');
  const [utmSource, setUtmSource] = useState('linkedin');
  const [utmMedium, setUtmMedium] = useState('social');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const word = slug.trim().toLowerCase();
  const destinationPath = pageChoice === OTHER_PAGE ? otherPage.trim() : pageChoice;

  const reset = () => {
    setSlug('');
    setPageChoice(SHORT_LINK_DEFAULT_DESTINATION);
    setOtherPage('');
    setUtmSource('linkedin');
    setUtmMedium('social');
    setUtmCampaign('');
    setErrors({});
    setServerError(null);
  };

  const create = useMutation({
    mutationFn: (data: ShortLinkCreateRequest) => api.createShortLink(data),
    onSuccess: ({ link }) => {
      reset();
      onCreated(link);
    },
    onError: (err) => setServerError(crmErrorMessage(err)),
  });

  const validate = (): FieldErrors => {
    const next: FieldErrors = {};
    const checks = [
      ['slug', validateShortLinkSlug(word)],
      ['page', validateShortLinkDestination(destinationPath)],
      ['utmSource', validateShortLinkTag(utmSource.trim(), 'source')],
      ['utmMedium', validateShortLinkTag(utmMedium.trim(), 'medium')],
      ['utmCampaign', validateShortLinkTag(utmCampaign.trim(), 'campaign')],
    ] as const;
    for (const [field, result] of checks) {
      if (result.ok === false) next[field] = result.reason;
    }
    return next;
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setServerError(null);
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    create.mutate({
      slug: word,
      destinationPath,
      utmSource: utmSource.trim(),
      utmMedium: utmMedium.trim(),
      utmCampaign: utmCampaign.trim(),
    });
  };

  const preview =
    word && validateShortLinkDestination(destinationPath).ok && utmCampaign.trim()
      ? buildShortLinkTargetPath(destinationPath, {
          utmSource: utmSource.trim(),
          utmMedium: utmMedium.trim(),
          utmCampaign: utmCampaign.trim(),
        })
      : null;

  const close = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} size="xl">
      <form onSubmit={submit} noValidate>
        <DialogTitle>New short link</DialogTitle>
        <DialogDescription>
          The link opens the page you choose with these tags attached, so Mixpanel counts it like the long
          tagged link.
        </DialogDescription>
        <DialogBody>
          <div className="space-y-5">
            {serverError && (
              <div role="alert" className="rounded-lg bg-red-50 px-4 py-2.5 text-sm font-medium text-red-700 dark:bg-red-900/20 dark:text-red-400">
                {serverError}
              </div>
            )}

            <Field>
              <Label>Short word</Label>
              <Description>
                apptonomy.ai{SHORT_LINK_PATH_PREFIX}
                <span className="font-medium text-zinc-900 dark:text-white">{word || 'cal-ai'}</span>
              </Description>
              <Input
                name="slug"
                value={slug}
                autoFocus
                invalid={Boolean(errors.slug)}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSlug(e.target.value)}
                placeholder="cal-ai"
              />
              {errors.slug && <ErrorMessage>{errors.slug}</ErrorMessage>}
            </Field>

            <Field>
              <Label>Opens</Label>
              <Select
                name="page"
                value={pageChoice}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => setPageChoice(e.target.value)}
              >
                {SHORT_LINK_PAGE_CHOICES.map((choice) => (
                  <option key={choice.value} value={choice.value}>
                    {choice.label}
                  </option>
                ))}
                <option value={OTHER_PAGE}>Another page…</option>
              </Select>
              {pageChoice === OTHER_PAGE && (
                <Input
                  className="mt-2"
                  name="otherPage"
                  aria-label="Page path"
                  value={otherPage}
                  invalid={Boolean(errors.page)}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setOtherPage(e.target.value)}
                  placeholder="/for/agencies"
                />
              )}
              {errors.page && <ErrorMessage>{errors.page}</ErrorMessage>}
            </Field>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Field>
                <Label>Source</Label>
                <Input
                  name="utmSource"
                  value={utmSource}
                  invalid={Boolean(errors.utmSource)}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setUtmSource(e.target.value)}
                />
                {errors.utmSource && <ErrorMessage>{errors.utmSource}</ErrorMessage>}
              </Field>
              <Field>
                <Label>Medium</Label>
                <Input
                  name="utmMedium"
                  value={utmMedium}
                  invalid={Boolean(errors.utmMedium)}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setUtmMedium(e.target.value)}
                />
                {errors.utmMedium && <ErrorMessage>{errors.utmMedium}</ErrorMessage>}
              </Field>
              <Field>
                <Label>Campaign</Label>
                <Input
                  name="utmCampaign"
                  value={utmCampaign}
                  invalid={Boolean(errors.utmCampaign)}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setUtmCampaign(e.target.value)}
                  placeholder="teardown-cal-ai"
                />
                {errors.utmCampaign && <ErrorMessage>{errors.utmCampaign}</ErrorMessage>}
              </Field>
            </div>

            {preview && (
              <p className="break-all rounded-lg bg-zinc-50 px-3 py-2 text-xs text-zinc-600 dark:bg-zinc-800/60 dark:text-zinc-300">
                Opens apptonomy.ai{preview}
              </p>
            )}
          </div>
        </DialogBody>
        <DialogActions>
          <Button plain type="button" onClick={close}>
            Cancel
          </Button>
          <Button color="emerald" type="submit" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create link'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
